import {
  effect,
  exhaustMapWithTrailing,
  fromPromise,
  LiveData,
  onComplete,
  OnEvent,
  onStart,
  Service,
  smartRetry,
} from '@toeverything/infra';
import { catchError, EMPTY, tap } from 'rxjs';

import { AccountChanged } from '../events/account-changed';
import type { AccessToken, AccessTokenStore } from '../stores/access-token';

@OnEvent(AccountChanged, e => e.onAccountChanged)
export class AccessTokenService extends Service {
  constructor(private readonly accessTokenStore: AccessTokenStore) {
    super();
  }

  accessTokens$ = new LiveData<AccessToken[] | null>(null);
  isRevalidating$ = new LiveData(false);
  error$ = new LiveData<any>(null);

  // The server stores only a hash of the token, so it returns the plaintext
  // once, at creation. Every list after that returns [REDACTED]. Creation is
  // immediately followed by a revalidation, so the plaintext is overwritten
  // before anyone can read it and the MCP config always shows [REDACTED].
  // Keep the plaintext for this session and put it back on each list.
  private readonly plaintextTokens = new Map<string, string>();

  private withPlaintext(accessTokens: AccessToken[]) {
    return accessTokens.map(accessToken => {
      const plaintext = this.plaintextTokens.get(accessToken.id);
      return plaintext ? { ...accessToken, token: plaintext } : accessToken;
    });
  }

  async generateUserAccessToken(name: string) {
    const accessToken =
      await this.accessTokenStore.generateUserAccessToken(name);
    this.plaintextTokens.set(accessToken.id, accessToken.token);
    this.accessTokens$.value = [
      ...(this.accessTokens$.value || []),
      accessToken as AccessToken,
    ];

    await this.waitForRevalidation();
  }

  async revokeUserAccessToken(id: string) {
    await this.accessTokenStore.revokeUserAccessToken(id);
    this.plaintextTokens.delete(id);
    this.accessTokens$.value =
      this.accessTokens$.value?.filter(token => token.id !== id) ?? null;
    await this.waitForRevalidation();
  }

  revalidate = effect(
    exhaustMapWithTrailing(() => {
      return fromPromise(() => {
        return this.accessTokenStore.listUserAccessTokens();
      }).pipe(
        smartRetry(),
        tap(accessTokens => {
          this.accessTokens$.value = this.withPlaintext(accessTokens);
        }),
        catchError(error => {
          this.error$.value = error;
          return EMPTY;
        }),
        onStart(() => {
          this.isRevalidating$.value = true;
        }),
        onComplete(() => {
          this.isRevalidating$.value = false;
        })
      );
    })
  );

  private onAccountChanged() {
    this.plaintextTokens.clear();
    this.accessTokens$.value = null;
    this.revalidate();
  }

  async waitForRevalidation(signal?: AbortSignal) {
    this.revalidate();
    await this.isRevalidating$.waitFor(
      isRevalidating => !isRevalidating,
      signal
    );
  }
}
